import { Inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { from, Observable, Subscription, throwError } from 'rxjs';
import { map, catchError, finalize, switchMap } from 'rxjs/operators';
import { NavController } from '@ionic/angular';
import { LoadingService } from '../loading-service/loading.service';
import { SKIP_LOADING_OVERLAY } from '../loading-service/loading-context';
import {
  extractUrlNoParams,
  matchesHiddenLoadingUrl,
  treatmentQueueHiddenLoadingFragments,
} from '../loading-service/hidden-loading-urls';
import { Capacitor } from '@capacitor/core';
import { App, AppInfo } from '@capacitor/app';
import { ILocalAppEnvironment } from '../environment-config-service/environment-config-service';
import { UserData } from './models/user-data.model';
import { TokenService } from '../token/token.service';
import { Events } from '../events/events';
import { sharedConfig } from 'src/environments/shared-config';
import { AuthenticationEntraExternalDService } from 'src/app/api/proxy/authentication/services/authentication-entra-external-d.service';
import { EnvironmentAppEnvironmentService } from 'src/app/api/proxy/environment/services/environment-app-environment.service';
import { PracticePatientService } from 'src/app/api/proxy/practice/services/practice-patient.service';
import { AuthService } from './auth.service';
import { PracticeEncounterService } from 'src/app/api/proxy/practice/services/practice-encounter.service';
import { PracticeDataSyncService } from 'src/app/api/proxy/practice/services/practice-data-sync.service';
import { PracticeDeviceService } from 'src/app/api/proxy/practice/services/practice-device.service';
import {
  PracticeClinicGroupService,
  PracticePractitionerService,
} from 'src/app/api/proxy/practice/services';

@Injectable()
export class GlobalHttpInterceptorService implements HttpInterceptor, OnDestroy {
  appInfo?: AppInfo | null = null;
  appInfoLoaded = false;
  environment: ILocalAppEnvironment | null = null;
  currentUser: UserData | null = null;
  private isRefreshing = false; // Flag to check if token refresh is happening
  // BehaviorSubject to track whether all services are initialized
  private subscriptions: Subscription[] = [];
  private refreshPromise: Promise<string | null> | null = null;

  constructor(
    public navCtrl: NavController,
    private _TokenService: TokenService,
    private authService: AuthService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.subscriptions.push(
      Events.environmentLoaded.subscribe(async (env: ILocalAppEnvironment) => {
        this.environment = env;
      }),
    );
    this.subscriptions.push(
      Events.userUpdated.subscribe(async (usr: UserData | null) => {
        this.currentUser = usr;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((a) => {
      a.unsubscribe();
    });
  }

  // ───────────────────────────── intercept ─────────────────────────────

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isIgnoredUrl(req.url)) {
      return next.handle(req);
    }

    if (this.isPublicUrl(req.url)) {
      return from(this.addHeaders(req, null /*no token*/, true /*public*/)).pipe(
        switchMap((cloned) => this.handleRequest(cloned, next)),
      );
    }

    return from(this.buildPrivateRequest(req)).pipe(
      switchMap(({ request, hadAuth }) => this.handleRequest(request, next, hadAuth)),
    );
  }

  /** Constructs a private request. Adds Authorization if available; otherwise none. */
  private async buildPrivateRequest(
    req: HttpRequest<any>,
  ): Promise<{ request: HttpRequest<any>; hadAuth: boolean }> {
    // try to get a token (do NOT force-refresh here)
    let token: string | null = null;
    try {
      token = await this._TokenService.getTokenAsync();
    } catch {
      token = null;
    }

    // if no token yet, still proceed without auth (as you requested)
    const hadAuth = !!token;
    const cloned = await this.addHeaders(req, token, false /*public*/);
    return { request: cloned, hadAuth };
  }

  // ─────────────────────────── helpers/core ───────────────────────────

  /** Wraps the actual HTTP call with loader + 401 handling. */
  private handleRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
    hadAuth = false,
  ): Observable<HttpEvent<any>> {
    if (!this.isHiddenUrl(request.url) && !request.context.get(SKIP_LOADING_OVERLAY)) {
      LoadingService.setLoading(true, request.url);
    }

    return next.handle(request).pipe(
      map((evt) => {
        if (evt instanceof HttpResponse) {
          // success path hook if needed
        }
        return evt as HttpEvent<any>;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // If we never had Authorization on this request (no session), skip refresh flow.
          if (!hadAuth) {
            this._TokenService.logout(`${error.status} on unauthenticated request`).finally(() => {
              return throwError(() => error);
            });
          }
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        LoadingService.setLoading(false, request.url);
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // ─────────────────────────── 401 handling ───────────────────────────

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // do not refresh if this is the refresh endpoint itself (avoid loops)
    const refreshPath = this.extractUrlNoParams(
      AuthenticationEntraExternalDService.EntraExternalDRefreshTokenPostPath,
    );
    if (request.url.includes(refreshPath)) {
      return throwError(() => new Error('401 on refresh endpoint'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshPromise = this._TokenService.refreshTokenAsync();

      if (!this.refreshPromise) {
        this.isRefreshing = false;
        return throwError(() => new Error('No refresh promise created'));
      }

      return from(this.refreshPromise).pipe(
        switchMap((newToken) => {
          this.isRefreshing = false;
          this.refreshPromise = null;

          if (!newToken) {
            return throwError(() => new Error('Token refresh returned null'));
          }

          return from(this.addHeaders(request, newToken, false)).pipe(
            switchMap((cloned) => next.handle(cloned)),
          );
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshPromise = null;

          this._TokenService.logout('401 during token refresh').finally(() => {});

          return throwError(() => err);
        }),
      );
    } else {
      // coalesce concurrent 401s while a refresh is in progress
      return from(this.refreshPromise!).pipe(
        switchMap((newToken) => {
          if (!newToken) {
            return throwError(() => new Error('Coalesced refresh returned null'));
          }
          return from(this.addHeaders(request, newToken, false)).pipe(
            switchMap((cloned) => next.handle(cloned)),
          );
        }),
        catchError((err) => throwError(() => err)),
      );
    }
  }

  // ───────────────────────── headers & utils ─────────────────────────

  /** Adds headers. If `isPublic`, never adds Authorization. */
  private async addHeaders(
    request: HttpRequest<any>,
    token: string | null,
    isPublic: boolean,
  ): Promise<HttpRequest<any>> {
    // default headers
    let headers = request.headers
      .set('user-language', this.document.documentElement.lang)
      .set('ngrok-skip-browser-warning', 'true');

    // app info (cached)
    if (!this.appInfo) {
      this.appInfo =
        Capacitor.getPlatform() !== 'web'
          ? await App.getInfo()
          : {
              name: window.location.hostname,
              id: sharedConfig.webAppId!,
              version: sharedConfig.version,
              build: sharedConfig.build.toString(),
            };
    }

    // app/env headers (safe for both public & private)
    if (this.appInfo && this.environment?.countryCode) {
      headers = headers
        .set('ion-app-deviceType', Capacitor.getPlatform())
        .set('ion-app-name', this.appInfo.name)
        .set('ion-app-id', this.appInfo.id)
        .set('ion-app-version', this.appInfo.version)
        .set('ion-app-build', this.appInfo.build)
        .set('ion-app-countryCode', this.environment.countryCode)
        .set('X-Bridge-Device-Id', this.authService.BridgeTokenId);
    }

    // Authorization only on private requests *and* only if a token was provided
    if (!isPublic && token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return request.clone({ headers });
  }

  private isPublicUrl(url: string): boolean {
    const result = [
      this.extractUrlNoParams(AuthenticationEntraExternalDService.EntraExternalDRequestOtpPostPath),
      this.extractUrlNoParams(
        AuthenticationEntraExternalDService.EntraExternalDVerifyOtpAsyncPostPath,
      ),
      this.extractUrlNoParams(
        AuthenticationEntraExternalDService.EntraExternalDForgotPasswordRequestPostPath,
      ),
      this.extractUrlNoParams(
        AuthenticationEntraExternalDService.EntraExternalDResetForgotPasswordPostPath,
      ),
      this.extractUrlNoParams(EnvironmentAppEnvironmentService.AppEnvironmentCountriesGetPath),
      this.extractUrlNoParams(EnvironmentAppEnvironmentService.AppEnvironmentSettingsPostPath),
      this.extractUrlNoParams(PracticeDeviceService.DeviceActivateDisplayDeviceLicenseCodePostPath),
    ];
    return result.some((p) => url.includes(p));
  }

  private isHiddenUrl(url: string): boolean {
    const result: string[] = [
      this.extractUrlNoParams(
        PracticePatientService.PatientClinicGroupIdPatientsIndexPageSizeGetPath,
      ),
      this.extractUrlNoParams(PracticeEncounterService.EncounterCompleteEncounterPostPath),
      this.extractUrlNoParams(PracticeDataSyncService.DataSyncLogFailedSyncTaskPostPath),
      this.extractUrlNoParams(
        AuthenticationEntraExternalDService.EntraExternalDGetLoginClinicGroupsPostPath
      ),
      ...treatmentQueueHiddenLoadingFragments(),
    ];
    return matchesHiddenLoadingUrl(url, result);
  }

  private isIgnoredUrl(url: string): boolean {
    const result: string[] = [
      '/AppLogMessage',
      '/i18n/',
      this.extractUrlNoParams(EnvironmentAppEnvironmentService.AppEnvironmentSettingsPostPath),
      this.extractUrlNoParams(PracticeDeviceService.DeviceActivateDisplayDeviceLicenseCodePostPath),
      this.extractUrlNoParams(EnvironmentAppEnvironmentService.AppEnvironmentCountriesGetPath),
      this.extractUrlNoParams(
        EnvironmentAppEnvironmentService.AppEnvironmentEnvironmentSetupEnvironmentNameCountryCodeDeviceTypeNameAppIdVersionBuildProductionGetPath,
      ),
      this.extractUrlNoParams(
        AuthenticationEntraExternalDService.EntraExternalDRefreshTokenPostPath,
      ),
      'https://api.onesignal.com/'
    ];
    return result.some((p) => url.includes(p));
  }

  private extractUrlNoParams(url: string): string {
    return extractUrlNoParams(url);
  }
}
